import { makeObservable, observable, action, computed, runInAction } from 'mobx';
import dayjs from 'dayjs';

//
// A finite state machine.
// Determines actions the user can take,
// hiding / showing buttons and tools
//
class UserFlowState {
    api=null;

    initialized=false;

    acceptedTerms = false;
    registrationComplete = false;

    calculateRequired = null;
    partialCalculateRequired = null;
    calculatePostponedForLocation = null;  // do not calculate automatically on this location
    calculationProgress = null;  // null | "started" | "finished"

    allQuestionsAnswered = false;
    someLabTestsFilled = false;
    risksVisited = false;
    agesVisited = false;
    planVisited = false;

    video_2024_08_watched=false;


    // globalPanel
    globalPanel = null;
    panelsCount = null;

    constructor() {

        makeObservable(this, {
	    initialized: observable,
            //
            // Flow flags
            //
            // - profile-related
            acceptedTerms: observable,
            registrationComplete: observable,
            //
            globalPanel: observable,
            // - computed on backend, from filled data
            panelsCount: observable,
            allQuestionsAnswered: observable,
            someLabTestsFilled: observable,
            // - set by frontend
            risksVisited: observable,
            agesVisited: observable,
            planVisited: observable,
            //
            // Calculation state
            //
            calculateRequired: observable,
            partialCalculateRequired: observable,
            calculatePostponedForLocation: observable,
            calculationProgress: observable,
            //
            // Triggers for UI
            //
            stepLabTestsActive: computed,
            stepRisksActive: computed,
            stepAgesActive: computed,
            stepPlanActive: computed,
            stepNextActive: computed,
            showPanelsAndDynamicsInMenu: computed,
            //
            // Actions
            //
            init: action,
            // User actions
            setCalculateRequired: action,
            setPartialCalculateRequired: action,
            startCalculation: action,
            // page visits
            markRisksVisited: action,
            markAgesVisited: action,
            markPlanVisited: action,

            video_2024_08_watched: observable,
            markIntroVideoWatched: action,

        });
    }

    init({globalPanel, panelsCount, user, flow}) {
        this.globalPanel = globalPanel;
        this.panelsCount = panelsCount;
        this.calculateRequired = globalPanel.calculate_required;
        this.partialCalculateRequired = globalPanel.partial_calculate_required;
        // account-related user flow flags
        this.acceptedTerms = user.terms_accepted;
        this.registrationComplete = user.birthday && user.gender;
        // user flow flags returned by API, derived from actions
        this.allQuestionsAnswered = flow.active_panel.all_questions_answered;
        this.someLabTestsFilled = flow.active_panel.some_lab_tests_filled;
        //
        // user flow flags set by API
        //
        // globally for user
        this.video_2024_08_watched = flow.global.video_2024_08_watched ?? false;
        // for panel:
        this.risksVisited = flow.active_panel.risks_visited;
        this.agesVisited = flow.active_panel.ages_visited;
        this.planVisited = flow.active_panel.plan_visited;
	this.initialized=true;
    }

    setCalculateRequired({isRequired, postponeForLocation}) {
        this.calculateRequired = isRequired;
        this.partialCalculateRequired = isRequired;
        this.calculatePostponedForLocation = postponeForLocation || null;
    }

    setPartialCalculateRequired({isRequired, postponeForLocation}) {
        this.partialCalculateRequired = isRequired;
        this.calculatePostponedForLocation = postponeForLocation || null;
    }

    //
    // idempotent call
    //
    startCalculation() {
        if (!this.calculationProgress) {
            // start fetch
            this.api.request(`cabinet/panels/${this.globalPanel.id}/calculate/`, {}, { method: 'PUT' })
	    .then(data => {
                this.calculationProgress = "finished";
                this.calculatePostponedForLocation = null;
                if (data.message) {
                    alert(data.message);
                }
                location.reload();
            });
        }
        this.calculationProgress = "started";
    }


    //
    // Menu items activity flags
    //

    get stepLabTestsActive() {
        return this.allQuestionsAnswered;
    }
    get showPanelsAndDynamicsInMenu() {
        // Questions are usually copied to the next panel,
        // so it's redundant. But can be userful for later.
        return this.allQuestionsAnswered || this.panelsCount > 1;
    }
    get stepRisksActive() {
        return this.stepLabTestsActive && this.someLabTestsFilled;
    }
    get stepAgesActive() {
        return this.stepRisksActive && this.risksVisited;
    }
    get stepPlanActive() {
        return this.stepAgesActive && this.agesVisited;
    }
    get stepNextActive() {
        return this.stepPlanActive && this.planVisited;
    }

    //
    // Additional flags
    //

    get retakeQuestionsNotice() {
        return this.someLabTestsFilled && !this.allQuestionsAnswered;
    }


    //
    // User action setters
    //

    markRisksVisited() {
        if (!this.risksVisited) {
            this.api.request(`cabinet/panels/${this.globalPanel.id}/flow/`, {risks_visited: dayjs().toISOString() /* XXX actual timestamp is determined on server */}, { method: 'PATCH', })
	    .then(data => {
                this.risksVisited = true;
            });
        }
    }
    markAgesVisited() {
        if (!this.agesVisited) {
            this.api.request(`cabinet/panels/${this.globalPanel.id}/flow/`, {ages_visited: dayjs().toISOString() /* XXX actual timestamp is determined on server */},{ method: 'PATCH', })
	    .then(data => {
                this.agesVisited = true;
            });
        }
    }
    markPlanVisited() {
        if (!this.planVisited) {
            this.api.request(`cabinet/panels/${this.globalPanel.id}/flow/`, {plan_visited: dayjs().toISOString() /* XXX actual timestamp is determined on server */},{ method: 'PATCH', })
	    .then(data => {
                this.planVisited = true;
            });
        }
    }

    markIntroVideoWatched() {
        if (!this.video_2024_08_watched) {
            return this.api.request(`cabinet/flow/`, {video_2024_08_watched: dayjs().toISOString() /* XXX actual timestamp is determined on server */},{ method: 'PATCH', })
	    .then(data => {
		runInAction(() => { this.video_2024_08_watched = data.video_2024_08_watched; });
            });
        }
        return Promise.resolve();
    }
}

export const userFlowState = new UserFlowState();
export function injectApi(api)
{
	userFlowState.api = api;
}
